import { useEffect, useState, useCallback, Fragment, useMemo } from "react";
import api, { fmtEUR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, ShieldCheck, AlertCircle, Wallet, FileText, Clock, Users, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function toISO(d) { return d.toISOString().slice(0, 10); }

function SupplierDashboard() {
  const { branchFilter } = useAuth();
  const now = new Date();
  const [dfrom, setDfrom] = useState(toISO(new Date(now.getTime() - 29 * 86400000)));
  const [dto, setDto] = useState(toISO(now));
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(false);

  const toggleExpand = async (sid) => {
    if (expanded === sid) {
      setExpanded(null);
      return;
    }
    setExpanded(sid);
    setLoadingDetail(true);
    try {
      const r = await api.get(`/suppliers/${sid}/invoices?dfrom=${dfrom}&dto=${dto}`);
      setExpandedInvoices((prev) => ({ ...prev, [sid]: r.data }));
    } catch {
      setExpandedInvoices((prev) => ({ ...prev, [sid]: [] }));
    } finally {
      setLoadingDetail(false);
    }
  };

  const load = useCallback(() => {
    const p = new URLSearchParams({ dfrom, dto });
    if (branchFilter) p.set("branch_id", branchFilter);
    api.get(`/suppliers/dashboard?${p}`).then((r) => setData(r.data)).catch(() => {});
  }, [dfrom, dto, branchFilter]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};

  return (
    <div className="space-y-6" data-testid="suppliers-dashboard">
      <Card>
        <CardContent className="p-4 flex items-end gap-3 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs">Desde</Label>
            <Input data-testid="supdash-from" type="date" value={dfrom} onChange={(e) => setDfrom(e.target.value)} className="w-40 h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Hasta</Label>
            <Input data-testid="supdash-to" type="date" value={dto} onChange={(e) => setDto(e.target.value)} className="w-40 h-9" />
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Gasto total", value: fmtEUR(t.spent), icon: Wallet, testid: "supdash-spent" },
          { label: "Facturas", value: t.invoices ?? "—", icon: FileText, testid: "supdash-invoices" },
          { label: "Pendiente de pago", value: fmtEUR(t.pending), icon: Clock, testid: "supdash-pending" },
          { label: "Proveedores con deuda", value: t.suppliers_with_debt ?? "—", icon: Users, testid: "supdash-debtors" },
        ].map((k) => (
          <Card key={k.testid} data-testid={k.testid}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">{k.label}</CardTitle>
              <k.icon className="w-4 h-4 text-slate-400" />
            </CardHeader>
            <CardContent><div className="text-2xl font-extrabold font-num tracking-tight">{k.value}</div></CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base font-semibold font-heading">Gasto por proveedor</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proveedor</TableHead>
                <TableHead>Sucursal</TableHead>
                <TableHead className="text-right">Facturas</TableHead>
                <TableHead className="text-right">Gastado</TableHead>
                <TableHead className="text-right">Se le debe</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.suppliers || []).map((s) => (
                <Fragment key={s.supplier_id}>
                  <TableRow data-testid={`supdash-row-${s.supplier_id}`}
                    className="cursor-pointer hover:bg-slate-50" onClick={() => toggleExpand(s.supplier_id)}>
                    <TableCell className="text-sm font-medium">
                      <span className="text-slate-400 mr-1.5">{expanded === s.supplier_id ? "▾" : "▸"}</span>
                      {s.supplier_name}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{s.branch_name}</TableCell>
                    <TableCell className="text-right font-num">{s.invoice_count}</TableCell>
                    <TableCell className="text-right font-num font-semibold">{fmtEUR(s.total)}</TableCell>
                    <TableCell className={`text-right font-num ${s.pending > 0 ? "text-amber-700 font-semibold" : "text-slate-400"}`}>
                      {fmtEUR(s.pending)}
                    </TableCell>
                  </TableRow>
                  {expanded === s.supplier_id && (
                    <TableRow className="bg-slate-50">
                      <TableCell colSpan={5}>
                        {loadingDetail ? (
                          <div className="text-xs text-slate-400 py-3 text-center">Cargando facturas...</div>
                        ) : (expandedInvoices[s.supplier_id] || []).length === 0 ? (
                          <div className="text-xs text-slate-400 py-3 text-center">Sin facturas en este periodo</div>
                        ) : (
                          <div className="space-y-1 py-1" data-testid={`supdash-detail-${s.supplier_id}`}>
                            {(expandedInvoices[s.supplier_id] || []).map((inv) => (
                              <div key={inv.id} className="flex items-center justify-between bg-white border rounded-lg px-3 py-1.5 text-sm">
                                <span className="text-slate-600 font-num text-xs">{inv.invoice_number || inv.file_name}</span>
                                <span className="text-xs text-slate-500">{inv.invoice_date ? new Date(inv.invoice_date + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</span>
                                <span className="font-num">{fmtEUR(inv.total)}</span>
                                <span className={`font-num text-xs ${inv.total === null || inv.total === undefined ? "text-slate-400" : inv.pending > 0 ? "text-amber-700" : "text-green-700"}`}>
                                  {inv.total === null || inv.total === undefined ? "sin importe" : inv.pending > 0 ? `debe ${fmtEUR(inv.pending)}` : "pagada"}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
              {(data?.suppliers || []).length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400 text-sm">
                  Sin gasto registrado en este periodo.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Proveedores() {
  const { org } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyPending, setOnlyPending] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [supInvoices, setSupInvoices] = useState(null);
  const [previewInv, setPreviewInv] = useState(null);
  const branchNames = { ...(Object.fromEntries(org.branches.map((b) => [b.id, b.name]))) };

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set("q", search);
    if (onlyPending) params.set("pending", "true");
    api.get(`/suppliers?${params}`).then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, [search, onlyPending]);

  useEffect(() => { load(); }, [load]);
  const expenseCategories = useMemo(() => categories.filter((c) => c.group !== "otros_ingresos"), [categories]);

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "", tax_id: s.tax_id || "", category_id: s.category_id || "",
      account_code: s.account_code || "", iban: s.iban || "", payment_days: s.payment_days ?? "",
      default_branch_id: s.default_branch_id || "", notes: s.notes || "",
    });
    setSupInvoices(s.id ? null : []);
    if (s.id) {
      api.get(`/invoices?supplier_id=${s.id}`).then((r) => setSupInvoices(r.data)).catch(() => setSupInvoices([]));
    }
  };

  const openInvPreview = async (inv) => {
    if (!inv.has_file && !inv.storage_path) {
      toast.warning("Esta factura no tiene documento adjunto");
      return;
    }
    try {
      const r = await api.get(`/invoices/${inv.id}/file`, { responseType: "blob" });
      setPreviewInv({ inv, url: URL.createObjectURL(r.data) });
    } catch {
      toast.error("No se pudo cargar el documento");
    }
  };

  const downloadInv = () => {
    if (!previewInv) return;
    const a = document.createElement("a");
    a.href = previewInv.url;
    a.download = previewInv.inv.file_name || `factura_${previewInv.inv.id.slice(0, 8)}.pdf`;
    a.click();
  };

  const save = async () => {
    try {
      const payload = { ...form, category_id: form.category_id || null, default_branch_id: form.default_branch_id || null,
        payment_days: form.payment_days === "" ? null : Number(form.payment_days) };
      if (editing?.id) {
        const r = await api.patch(`/suppliers/${editing.id}`, payload);
        const n = r.data.invoices_updated || 0;
        toast.success(n > 0
          ? `Proveedor actualizado — ${n} factura${n === 1 ? "" : "s"} actualizada${n === 1 ? "" : "s"} automáticamente`
          : "Proveedor actualizado — clasificación definitiva");
      } else {
        await api.post("/suppliers", payload);
        toast.success("Proveedor creado");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const validateSupplier = async () => {
    try {
      await api.patch(`/suppliers/${editing.id}`, { pending_validation: false });
      toast.success("Proveedor validado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al validar");
    }
  };

  return (
    <div className="space-y-6" data-testid="proveedores-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Proveedores</h1>
          <p className="text-sm text-slate-500 mt-1">La sucursal del proveedor se hereda automáticamente en sus facturas</p>
        </div>
        <Button data-testid="new-supplier-button" onClick={() => openEdit({})} className="bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-2">
          <Plus className="w-4 h-4" /> Nuevo proveedor
        </Button>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList data-testid="suppliers-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-suppliers-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-suppliers-list">Listado</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6"><SupplierDashboard /></TabsContent>

        <TabsContent value="list" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-base font-semibold font-heading">{suppliers.length} {suppliers.length === 1 ? "proveedor" : "proveedores"}</CardTitle>
              <div className="flex gap-2 items-center">
                <Input data-testid="suppliers-search" placeholder="Buscar nombre o CIF..." value={search}
                  onChange={(e) => setSearch(e.target.value)} className="w-64 h-9" />
                <Button data-testid="suppliers-pending-filter" variant={onlyPending ? "default" : "outline"} size="sm"
                  onClick={() => setOnlyPending(!onlyPending)}
                  className={onlyPending ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}>
                  Pendientes de clasificar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Estado</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>CIF/NIF</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Sucursal</TableHead>
                    <TableHead className="text-right">Días pago</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.map((s) => (
                    <TableRow key={s.id} data-testid={`supplier-row-${s.id}`} className="cursor-pointer" onClick={() => openEdit(s)}>
                      <TableCell>
                        {s.pending_validation && (
                          <Badge variant="outline" data-testid={`supplier-pending-validation-${s.id}`}
                            className="text-red-700 border-red-200 bg-red-50 gap-1 mr-1"><AlertCircle className="w-3 h-3" />Por validar</Badge>
                        )}
                        {s.reviewed ? (
                          <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50 gap-1"><ShieldCheck className="w-3 h-3" />Revisado</Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 gap-1"><AlertCircle className="w-3 h-3" />Pendiente</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm font-medium">{s.name}</TableCell>
                      <TableCell className="font-num text-xs">{s.tax_id || "—"}</TableCell>
                      <TableCell className="text-sm">{s.category_name || "—"}</TableCell>
                      <TableCell className="text-sm">{branchNames[s.default_branch_id] || "Todas"}</TableCell>
                      <TableCell className="text-right font-num">{s.payment_days ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                  {suppliers.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400 text-sm">
                      Sin proveedores. Se crean automáticamente al procesar facturas.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="supplier-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing?.id ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div className="col-span-2 space-y-1.5">
              <Label>Nombre</Label>
              <Input data-testid="supplier-name-input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>CIF/NIF</Label>
              <Input data-testid="supplier-taxid-input" value={form.tax_id || ""} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Categoría</Label>
              <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="supplier-category-select"><SelectValue placeholder="Sin clasificar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin clasificar</SelectItem>
                  {expenseCategories.map((c) => <SelectItem key={c.id} value={c.id}>{c.group} · {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Sucursal habitual</Label>
              <Select value={form.default_branch_id || "all"} onValueChange={(v) => setForm({ ...form, default_branch_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="supplier-branch-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {org.branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Días de pago</Label>
              <Input data-testid="supplier-paymentdays-input" type="number" value={form.payment_days ?? ""} onChange={(e) => setForm({ ...form, payment_days: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Cuenta contable</Label>
              <Input data-testid="supplier-account-input" value={form.account_code || ""} onChange={(e) => setForm({ ...form, account_code: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>IBAN</Label>
              <Input data-testid="supplier-iban-input" value={form.iban || ""} onChange={(e) => setForm({ ...form, iban: e.target.value })} />
            </div>
          </div>
          {editing?.id && (
            <div className="mt-5 border-t pt-4" data-testid="supplier-invoices-section">
              <div className="text-sm font-semibold mb-2">Facturas del proveedor {supInvoices ? `(${supInvoices.length})` : ""}</div>
              {supInvoices === null && <p className="text-xs text-slate-400">Cargando facturas...</p>}
              {supInvoices && supInvoices.length === 0 && <p className="text-xs text-slate-400">Sin facturas registradas.</p>}
              {supInvoices && supInvoices.length > 0 && (
                <div className="max-h-56 overflow-y-auto border rounded-lg" data-testid="supplier-invoices-list">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nº factura</TableHead>
                        <TableHead>Fecha</TableHead>
                        <TableHead className="text-right">Importe</TableHead>
                        <TableHead>Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {supInvoices.map((i) => (
                        <TableRow key={i.id} data-testid={`supplier-invoice-row-${i.id}`} className="cursor-pointer hover:bg-slate-50"
                          onClick={() => openInvPreview(i)}>
                          <TableCell className="font-num text-xs">{i.invoice_number || "—"}</TableCell>
                          <TableCell className="text-xs">{i.invoice_date || "—"}</TableCell>
                          <TableCell className="text-right font-num text-sm">{fmtEUR(i.total)}</TableCell>
                          <TableCell className="text-xs text-slate-500">{i.status === "validated" ? "Validada" : i.status === "pending_review" ? "Pendiente" : i.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <p className="text-xs text-slate-400 mt-1.5">Haz clic en una factura para previsualizar su documento sin salir de aquí.</p>
            </div>
          )}
          <div className="flex justify-between gap-2 mt-4">
            <div>
              {editing?.id && editing?.pending_validation && (
                <Button data-testid="supplier-validate-button" variant="outline"
                  className="text-green-700 border-green-300 hover:bg-green-50" onClick={validateSupplier}>
                  Marcar validado
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button data-testid="supplier-save-button" onClick={save} className="bg-[#D9381E] hover:bg-[#B91C1C] text-white">
                Guardar{editing?.id ? " y marcar revisado" : ""}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewInv} onOpenChange={(o) => { if (!o) { if (previewInv?.url) URL.revokeObjectURL(previewInv.url); setPreviewInv(null); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh]" data-testid="supplier-invoice-preview">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Factura {previewInv?.inv.invoice_number || ""} · {previewInv?.inv.invoice_date || ""} · {fmtEUR(previewInv?.inv.total)}
            </DialogTitle>
          </DialogHeader>
          {previewInv && (
            <div className="space-y-3">
              <iframe src={previewInv.url} title="Vista previa de la factura" className="w-full h-[60vh] rounded-lg border border-slate-200"
                data-testid="supplier-invoice-preview-frame" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" data-testid="supplier-invoice-download" onClick={downloadInv} className="gap-1.5">
                  <Download className="w-3.5 h-3.5" /> Descargar
                </Button>
                <Button asChild size="sm" data-testid="supplier-invoice-goto"
                  className="bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-1.5">
                  <a href={`/facturas?invoice=${previewInv.inv.id}`}>
                    <ExternalLink className="w-3.5 h-3.5" /> Ver detalle completo
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
